// ─────────────────────────────────────────────────────────
// components/CommandMenu.tsx
// ⌘K / Ctrl+K global command palette
// Fuzzy-searches all nav items + quick actions.
//
// Phase 16: also searches live IDB data (projects, vendors,
// employees) so users can jump directly to a record's list.
// ─────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useDataStore } from '@/store/dataStore'
import { MODULE_ICONS } from '@/layouts/nav/navData'
import type { SBIconDef } from '@/layouts/nav/navData'
import type { Project, Vendor, Employee } from '@/types'

export interface CommandItem {
  id:        string
  label:     string
  sub?:      string      // section / category label
  iconKey?:  string      // MODULE_ICONS key
  pg:        string      // route path (without leading /)
  keywords?: string[]
}

function CmdIcon({ def, active }: { def: SBIconDef; active: boolean }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:24, height:24, borderRadius:5, background: active ? 'rgba(255,255,255,0.15)' : 'var(--color-sf)', flexShrink:0 }}>
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke={active ? '#fff' : 'var(--color-tx3)'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        {def.complex
          ? <g dangerouslySetInnerHTML={{ __html: def.complex }} />
          : <>{def.d && <path d={def.d} />}{def.extra && <path d={def.extra} />}</>}
      </svg>
    </span>
  )
}

// All navigable pages — mirrors NAV_SECTIONS in AppLayout
const ALL_ITEMS: CommandItem[] = [
  // Overview
  { id: 'dashboard',    label: 'Dashboard',            sub: 'Overview',    iconKey: 'dashboard',     pg: 'dashboard' },
  { id: 'projects',     label: 'Projects',             sub: 'Overview',    iconKey: 'projects',      pg: 'projects' },
  { id: 'boq',          label: 'BOQ Tracker',          sub: 'Overview',    iconKey: 'boq',           pg: 'boq' },
  { id: 'milestones',   label: 'Milestones',           sub: 'Overview',    iconKey: 'milestones',    pg: 'milestones' },
  { id: 'daily',        label: 'Daily Progress (DPR)', sub: 'Overview',    iconKey: 'daily',         pg: 'daily', keywords: ['dpr', 'daily report'] },
  { id: 'planner',      label: 'Tasks / Planner',      sub: 'Overview',    iconKey: 'planner',       pg: 'planner', keywords: ['task', 'todo'] },
  // Tenders & Billing
  { id: 'tender',       label: 'Tenders & CS',         sub: 'Tenders',     iconKey: 'tender',        pg: 'tender', keywords: ['comparative statement', 'nit', 'bid'] },
  { id: 'emd',          label: 'EMD Tracker',          sub: 'Tenders',     iconKey: 'emd',           pg: 'emd', keywords: ['earnest money'] },
  { id: 'receivables',  label: 'Receivables',          sub: 'Tenders',     iconKey: 'receivables',   pg: 'receivables', keywords: ['ra bill', 'billing', 'receipt'] },
  { id: 'retention',    label: 'Retention Release',    sub: 'Tenders',     iconKey: 'retention',     pg: 'retention' },
  { id: 'einvoice',     label: 'GST e-Invoice',        sub: 'Tenders',     iconKey: 'einvoice',      pg: 'einvoice', keywords: ['gst', 'irn', 'ack'] },
  // Finance
  { id: 'expenses',     label: 'Expenses',             sub: 'Finance',     iconKey: 'expenses',      pg: 'expenses' },
  { id: 'cashflow',     label: 'Cash Flow',            sub: 'Finance',     iconKey: 'cashflow',      pg: 'cashflow', keywords: ['inflow', 'outflow'] },
  { id: 'profitability',label: 'Profitability',        sub: 'Finance',     iconKey: 'profitability', pg: 'profitability', keywords: ['profit', 'loss', 'p&l'] },
  { id: 'pettycash',    label: 'Petty Cash',           sub: 'Finance',     iconKey: 'pettycash',     pg: 'pettycash' },
  { id: 'bankrecon',    label: 'Bank Reconciliation',  sub: 'Finance',     iconKey: 'bankrecon',     pg: 'bankrecon', keywords: ['recon', 'bank'] },
  // Procurement
  { id: 'vendors',      label: 'Vendors',              sub: 'Procurement', iconKey: 'vendors',       pg: 'vendors', keywords: ['supplier', 'subcontractor'] },
  { id: 'po',           label: 'Purchase Orders',      sub: 'Procurement', iconKey: 'po',            pg: 'po', keywords: ['grn', 'goods receipt'] },
  { id: 'quotations',   label: 'Quotations',           sub: 'Procurement', iconKey: 'quotations',    pg: 'quotations', keywords: ['quote', 'estimate'] },
  { id: 'invoices',     label: 'Contractor Invoices',  sub: 'Procurement', iconKey: 'invoices',      pg: 'invoices', keywords: ['vendor invoice'] },
  // HR & Payroll
  { id: 'employees',    label: 'Employees',            sub: 'HR',          iconKey: 'employees',     pg: 'employees', keywords: ['staff', 'worker'] },
  { id: 'attendance',   label: 'Attendance',           sub: 'HR',          iconKey: 'attendance',    pg: 'attendance', keywords: ['present', 'absent'] },
  { id: 'salary',       label: 'Salary',               sub: 'HR',          iconKey: 'salary',        pg: 'salary', keywords: ['payroll', 'wages'] },
  { id: 'slips',        label: 'Salary Slips',         sub: 'HR',          iconKey: 'slips',         pg: 'slips', keywords: ['pay slip'] },
  { id: 'advances',     label: 'Advances',             sub: 'HR',          iconKey: 'advances',      pg: 'advances', keywords: ['loan'] },
  { id: 'pfreg',        label: 'PF & ESIC',            sub: 'HR',          iconKey: 'pfreg',         pg: 'pfreg', keywords: ['provident fund', 'epfo', 'ecr'] },
  { id: 'insurance',    label: 'Insurance',            sub: 'HR',          iconKey: 'insurance',     pg: 'insurance' },
  // Compliance
  { id: 'tds',          label: 'TDS Ledger',           sub: 'Compliance',  iconKey: 'tds',           pg: 'tds', keywords: ['tax deducted'] },
  { id: 'auditlog',     label: 'Audit Log',            sub: 'Compliance',  iconKey: 'auditlog',      pg: 'auditlog' },
  // Settings
  { id: 'users',        label: 'User Management',      sub: 'Settings',    iconKey: 'users',         pg: 'users' },
  { id: 'billing',      label: 'Billing & Plan',       sub: 'Settings',    iconKey: 'billing',       pg: 'billing' },
  { id: 'branding',     label: 'Branding',             sub: 'Settings',    iconKey: 'branding',      pg: 'branding', keywords: ['logo', 'company'] },
  { id: 'bulkimport',   label: 'Bulk Import',          sub: 'Settings',    iconKey: 'bulkimport',    pg: 'bulkimport', keywords: ['csv', 'excel', 'import'] },
  { id: 'notifications',label: 'Notifications',        sub: 'Settings',    iconKey: 'notifications', pg: 'notifications' },
  { id: 'reports',      label: 'Reports',              sub: 'Settings',    iconKey: 'reports',       pg: 'reports', keywords: ['pdf', 'export'] },
  { id: 'settings',     label: 'Settings',             sub: 'Settings',    iconKey: 'settings',      pg: 'settings' },
]

// Simple fuzzy scorer — returns match score or -1 if no match
function score(item: CommandItem, q: string): number {
  if (!q) return 0
  const ql = q.toLowerCase()
  const targets = [
    item.label.toLowerCase(),
    item.sub?.toLowerCase() ?? '',
    item.pg.toLowerCase(),
    ...(item.keywords?.map(k => k.toLowerCase()) ?? []),
  ]
  for (const t of targets) {
    if (t === ql)                  return 100
    if (t.startsWith(ql))          return 80
    if (t.includes(ql))            return 60
    // character subsequence
    let ti = 0
    for (let qi = 0; qi < ql.length; qi++) {
      ti = t.indexOf(ql[qi]!, ti)
      if (ti === -1) break
      ti++
    }
    if (ti !== -1) return 30
  }
  return -1
}

interface Props {
  open:    boolean
  onClose: () => void
}

export default function CommandMenu({ open, onClose }: Props) {
  const navigate              = useNavigate()
  const [query, setQuery]     = useState('')
  const [active, setActive]   = useState(0)
  const inputRef              = useRef<HTMLInputElement>(null)
  const listRef               = useRef<HTMLDivElement>(null)

  // ── Live IDB data items (Phase 16) ──────────────────
  const { proj, vendor, emp } = useDataStore()

  const dataItems = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = []
    // Projects
    ;(proj as Project[]).slice(0, 50).forEach((p) => {
      items.push({
        id:       `proj-${p.id}`,
        label:    p.name,
        sub:      'Project',
        iconKey:  'projects',
        pg:       'projects',
        keywords: [p.client, p.location ?? ''].filter(Boolean),
      })
    })
    // Vendors
    ;(vendor as Vendor[]).slice(0, 50).forEach((v) => {
      items.push({
        id:       `vendor-${v.id}`,
        label:    v.name,
        sub:      'Vendor',
        iconKey:  'vendors',
        pg:       'vendors',
        keywords: [v.type].filter(Boolean),
      })
    })
    // Employees
    ;(emp as Employee[]).slice(0, 50).forEach((e) => {
      items.push({
        id:       `emp-${e.id}`,
        label:    e.name,
        sub:      'Employee',
        iconKey:  'employees',
        pg:       'employees',
        keywords: [e.designation ?? '', e.type].filter(Boolean),
      })
    })
    return items
  }, [proj, vendor, emp])

  // Filter + rank
  const results = useMemo(() => {
    if (!query.trim()) return ALL_ITEMS.slice(0, 8)
    const q = query.trim()
    return [...ALL_ITEMS, ...dataItems]
      .map(item => ({ item, s: score(item, q) }))
      .filter(x => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 12)
      .map(x => x.item)
  }, [query, dataItems])

  // Focus input on open, reset state
  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  // Keyboard navigation
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(a => Math.min(a + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(a => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = results[active]
      if (item) go(item)
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`) as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const go = (item: CommandItem) => {
    navigate(`/${item.pg}`)
    onClose()
  }

  if (!open) return null

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 900,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: '18%', left: '50%', transform: 'translateX(-50%)',
        width: 'min(560px, 92vw)', zIndex: 901,
        background: 'var(--color-bg)',
        border: '1px solid var(--color-bd)',
        borderRadius: 12,
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        overflow: 'hidden',
      }}>
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--color-bd)' }}>
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="var(--color-tx3)" strokeWidth="2" style={{ flexShrink: 0 }}>
            <circle cx="8.5" cy="8.5" r="5.5"/><path d="M15 15l-3.5-3.5"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setActive(0) }}
            onKeyDown={handleKey}
            placeholder="Search pages, projects, vendors, employees…"
            aria-label="Search navigation and records"
            aria-autocomplete="list"
            aria-expanded={results.length > 0}
            role="combobox"
            autoComplete="off"
            style={{
              flex: 1, border: 'none', outline: 'none', fontSize: 15,
              background: 'transparent', color: 'var(--color-tx)',
            }}
          />
          <kbd style={{ fontSize: 10, padding: '2px 5px', background: 'var(--color-bg2)', border: '1px solid var(--color-bd)', borderRadius: 4, color: 'var(--color-tx3)', flexShrink: 0 }}>Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} role="listbox" aria-label="Search results" style={{ maxHeight: 360, overflowY: 'auto', padding: '6px 8px' }}>
          {results.length === 0 ? (
            <div role="status" style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--color-tx3)' }}>
              No results for "{query}"
            </div>
          ) : results.map((item, idx) => (
            <div
              key={item.id}
              role="option"
              aria-selected={idx === active}
              data-idx={idx}
              onClick={() => go(item)}
              onMouseEnter={() => setActive(idx)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                background: idx === active ? 'var(--color-primary)' : 'transparent',
                color: idx === active ? '#fff' : 'var(--color-tx)',
                transition: 'background .1s',
              }}
            >
              {item.iconKey && MODULE_ICONS[item.iconKey] && <CmdIcon def={MODULE_ICONS[item.iconKey]!} active={idx === active} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
              </div>
              <span style={{
                fontSize: 10, padding: '1px 6px', borderRadius: 4, flexShrink: 0,
                background: idx === active ? 'rgba(255,255,255,0.2)' : 'var(--color-bg2)',
                color: idx === active ? '#fff' : 'var(--color-tx3)',
                border: `1px solid ${idx === active ? 'rgba(255,255,255,0.2)' : 'var(--color-bd)'}`,
              }}>{item.sub}</span>
              {idx === active && (
                <kbd style={{ fontSize: 10, padding: '1px 5px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, color: '#fff', flexShrink: 0 }}>↵</kbd>
              )}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--color-bd)', display: 'flex', gap: 14, fontSize: 10.5, color: 'var(--color-tx3)' }}>
          <span><kbd style={{ padding: '1px 4px', background: 'var(--color-bg2)', border: '1px solid var(--color-bd)', borderRadius: 3 }}>↑↓</kbd> navigate</span>
          <span><kbd style={{ padding: '1px 4px', background: 'var(--color-bg2)', border: '1px solid var(--color-bd)', borderRadius: 3 }}>↵</kbd> open</span>
          <span><kbd style={{ padding: '1px 4px', background: 'var(--color-bg2)', border: '1px solid var(--color-bd)', borderRadius: 3 }}>Esc</kbd> close</span>
        </div>
      </div>
    </>,
    document.body
  )
}
